/* eslint-disable @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unused-vars, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-explicit-any, @typescript-eslint/strict-boolean-expressions */

import {
  RawGreeksDataMap,
  GREEK_IDX,
  GreeksData,
  OPTION_IDX,
  RawGreeksObject,
} from 'types';
import {
  TableData,
  VolatilityDataRow as TableDataRow,
  OptionLegType,
  ModelParameters,
  ProfitLossWithDomain,
  ProfitLossData,
  OptionLeg,
  TimeframeBoundsPoint,
} from 'types/riskEngine';
import * as d3 from 'd3';
import {
  black76,
  calculateVarianceTime,
  VarianceTimeCalendarModel,
  calculateImpliedVolatilities,
  calculateImpliedVolatilitiesWithOptionType,
  GreeksResult,
} from 'sg-risk-engine';
import {
  OptionType,
  PositionLeg,
  PositionShares,
} from 'types/optionsCalculator';
import {
  CALENDAR_DAYS_IN_YEAR,
  CLOSURES,
  dayjs,
  ET,
  MILLISECONDS_PER_DAY,
} from './shared/date';
import {
  convertToDaysTillExpiryIntraday,
  findD50Price,
  getMidpointPrice,
} from './iVol';
import { getRiskEngineConstants } from 'config/shared';
import { CONTRACT_MULTIPLIER } from 'config/riskEngine';
import {
  resolveStyleForExpiry,
  type ExerciseStyleName,
  type ExerciseStylesRecord,
} from './moviefoneExerciseStyles';

const { CALL, PUT } = OPTION_IDX;

export function getStrikePricesForFirstTimestamp(
  currentGreeksObj: any,
): number[] | undefined {
  if (!currentGreeksObj) {
    return undefined;
  }

  const timestamps = Object.keys(currentGreeksObj);
  if (timestamps.length === 0) {
    return undefined;
  }

  const firstTimestamp = timestamps[0];
  const firstTimestampObj = currentGreeksObj[firstTimestamp];
  const strikePrices = Object.keys(firstTimestampObj).map(Number);
  return strikePrices;
}

// Return DTE, price, column assuming the first timestamp is 0 DTE after getFilteredGreeksData
export function extractTableData(
  data: RawGreeksDataMap,
  isCall: boolean,
  column: number,
): TableData[] {
  const result: TableData[] = [];

  for (const tte in data) {
    const priceLevel: RawGreeksObject = data[tte];
    const timestampResult: TableData = {
      tte: Number(tte),
      strikePrices: {},
    };

    for (const price in priceLevel) {
      const value = priceLevel[price][isCall ? 0 : 1][column];
      timestampResult.strikePrices[Number(price)] = value;
    }

    result.push(timestampResult);
  }

  return result;
}

export function applyMovingAverage(
  values: number[],
  windowSize: number = 129,
): number[] {
  if (values.length <= windowSize) {
    return [...values];
  }

  const halfWindow = Math.floor(windowSize / 2);
  const result: number[] = new Array(values.length);

  // Pre-calculate the middle portion using sliding window
  let currentSum = 0;
  let currentWindowSize = 0;

  // Initialize the first window
  for (let i = 0; i < windowSize && i < values.length; i++) {
    currentSum += values[i];
    currentWindowSize++;
  }

  // Calculate middle portion with sliding window
  for (let i = halfWindow; i < values.length - halfWindow; i++) {
    result[i] = currentSum / currentWindowSize;

    // Slide the window: remove leftmost element, add rightmost element
    if (i + halfWindow + 1 < values.length) {
      currentSum -= values[i - halfWindow];
      currentSum += values[i + halfWindow + 1];
    }
  }

  // Handle left boundary with sliding window
  currentSum = 0;
  currentWindowSize = 0;

  for (let i = 0; i < halfWindow; i++) {
    const actualWindowSize = Math.min(i + 1, windowSize);
    const start = Math.max(0, i - Math.floor(actualWindowSize / 2));
    const end = Math.min(values.length, start + actualWindowSize);

    // Recalculate sum for this window
    currentSum = 0;
    currentWindowSize = 0;
    for (let j = start; j < end; j++) {
      currentSum += values[j];
      currentWindowSize++;
    }

    result[i] = currentSum / currentWindowSize;
  }

  // Handle right boundary with sliding window
  currentSum = 0;
  currentWindowSize = 0;

  for (let i = values.length - halfWindow; i < values.length; i++) {
    const actualWindowSize = Math.min(values.length - i, windowSize);
    const start = Math.max(0, i - Math.floor(actualWindowSize / 2));
    const end = Math.min(values.length, start + actualWindowSize);

    // Recalculate sum for this window
    currentSum = 0;
    currentWindowSize = 0;
    for (let j = start; j < end; j++) {
      currentSum += values[j];
      currentWindowSize++;
    }

    result[i] = currentSum / currentWindowSize;
  }

  return result;
}

// Helper function to calculate implied volatilities for both call and put options
function calculateImpliedVolatilitiesBothTypes(
  currentPrice: number,
  strikes: Float64Array,
  callPrices: Float64Array,
  putPrices: Float64Array,
  yte: number,
  riskFreeRate: number,
  timeFrameStart: number,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
  exerciseStyle: ExerciseStyleName = 'american',
): Float64Array {
  try {
    return calculateImpliedVolatilities(
      currentPrice,
      strikes,
      callPrices,
      putPrices,
      yte,
      riskFreeRate,
      timeFrameStart,
      Array.from(CLOSURES),
      dividendDates,
      dividendAmounts,
      true,
      exerciseStyle,
    );
  } catch (error) {
    return calculateImpliedVolatilities(
      currentPrice,
      strikes,
      callPrices,
      putPrices,
      yte,
      riskFreeRate,
      timeFrameStart,
      Array.from(CLOSURES),
      dividendDates,
      dividendAmounts,
      false,
      exerciseStyle,
    );
  }
}

// Helper function to calculate implied volatilities for a given option type
function calculateImpliedVolatilitiesForOptionType(
  currentPrice: number,
  strikes: Float64Array,
  callPrices: Float64Array,
  putPrices: Float64Array,
  yte: number,
  riskFreeRate: number,
  optionType: OptionType,
  exerciseStyle: ExerciseStyleName = 'american',
): Float64Array {
  try {
    return calculateImpliedVolatilitiesWithOptionType(
      currentPrice,
      strikes,
      callPrices,
      putPrices,
      yte,
      riskFreeRate,
      optionType === OptionType.CALL,
      exerciseStyle,
    );
  } catch (error) {
    return calculateImpliedVolatilitiesWithOptionType(
      currentPrice,
      strikes,
      callPrices,
      putPrices,
      yte,
      riskFreeRate,
      optionType === OptionType.CALL,
      exerciseStyle,
    );
  }
}

// Core helper function that processes volatility data
function processVolatilityData(
  greeksData: RawGreeksDataMap,
  currentPrice: number,
  getRiskFreeRate: (tte: number) => number,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
  exerciseStyles: ExerciseStylesRecord | null = null,
): TableData[] {
  const result: TableData[] = [];

  for (const tte in greeksData) {
    const priceLevel: RawGreeksObject = greeksData[tte];
    const timestampResult: TableData = {
      tte: Number(tte),
      strikePrices: {},
      d50Price: findD50Price(priceLevel),
    };

    const strikeMapEntries: [string, GreeksData][] = Object.entries(priceLevel);
    const strikes = strikeMapEntries.map(([strike, _]) => Number(strike));
    const [callPrices, putPrices] = [
      strikeMapEntries.map(([_, greeks]) => getMidpointPrice(greeks[CALL])),
      strikeMapEntries.map(([_, greeks]) => getMidpointPrice(greeks[PUT])),
    ];

    const timeFrameStart = dayjs().tz(ET);
    const yte = getTteYearsFromBaseDate(timeFrameStart.valueOf(), Number(tte));
    const exerciseStyle = resolveStyleForExpiry(exerciseStyles, Number(tte));
    const ivs = calculateImpliedVolatilitiesBothTypes(
      currentPrice,
      new Float64Array(strikes),
      new Float64Array(callPrices),
      new Float64Array(putPrices),
      yte,
      getRiskFreeRate(yte),
      timeFrameStart.unix(),
      dividendDates,
      dividendAmounts,
      exerciseStyle,
    );

    strikes.forEach((strike, i) => {
      timestampResult.strikePrices[strike] = ivs[i];
    });

    result.push(timestampResult);
  }

  return result;
}

export function getSGVolatilityData(
  data: RawGreeksDataMap | null,
  currentPrice: number | undefined,
  getRiskFreeRate: (tte: number) => number,
  dividendDates: Float64Array = new Float64Array(0),
  dividendAmounts: Float64Array = new Float64Array(0),
  exerciseStyles: ExerciseStylesRecord | null = null,
): TableData[] {
  if (!data || !currentPrice) {
    return [];
  }

  return processVolatilityData(
    data,
    currentPrice,
    getRiskFreeRate,
    dividendDates,
    dividendAmounts,
    exerciseStyles,
  );
}

function processVolatilityDataWithOptionType(
  greeksData: RawGreeksDataMap,
  currentPrice: number,
  getRiskFreeRate: (tte: number) => number,
  optionType: OptionType,
  exerciseStyles: ExerciseStylesRecord | null = null,
): TableData[] {
  const result: TableData[] = [];

  for (const tte in greeksData) {
    const priceLevel: RawGreeksObject = greeksData[tte];
    const timestampResult: TableData = {
      tte: Number(tte),
      strikePrices: {},
      d50Price: findD50Price(priceLevel),
    };

    const strikeMapEntries: [string, GreeksData][] = Object.entries(priceLevel);
    const strikes = strikeMapEntries.map(([strike, _]) => Number(strike));
    const [callPrices, putPrices] = [
      strikeMapEntries.map(([_, greeks]) => getMidpointPrice(greeks[CALL])),
      strikeMapEntries.map(([_, greeks]) => getMidpointPrice(greeks[PUT])),
    ];

    const timeFrameStart = dayjs().tz(ET);
    const yte = getTteYearsFromBaseDate(timeFrameStart.valueOf(), Number(tte));
    const exerciseStyle = resolveStyleForExpiry(exerciseStyles, Number(tte));
    const ivs = calculateImpliedVolatilitiesForOptionType(
      currentPrice,
      new Float64Array(strikes),
      new Float64Array(callPrices),
      new Float64Array(putPrices),
      yte,
      getRiskFreeRate(yte),
      optionType,
      exerciseStyle,
    );

    strikes.forEach((strike, i) => {
      timestampResult.strikePrices[strike] = ivs[i];
    });

    result.push(timestampResult);
  }

  return result;
}

export function getSGVolatilityDataWithOptionType(
  data: RawGreeksDataMap | null,
  optionType: OptionType,
  currentPrice: number | undefined,
  getRiskFreeRate: (tte: number) => number,
  exerciseStyles: ExerciseStylesRecord | null = null,
): TableData[] {
  if (!data || !currentPrice) {
    return [];
  }

  return processVolatilityDataWithOptionType(
    data,
    currentPrice,
    getRiskFreeRate,
    optionType,
    exerciseStyles,
  );
}

// Optimized interpolation over a row given a sequence of sorted prices
export function interpolateDataRow(
  data: TableDataRow[],
  prices: number[],
): number[] {
  let i = 0;

  return prices.map((price) => {
    while (i + 1 < data.length && data[i + 1].strike <= price) {
      ++i;
    }

    if (i + 1 >= data.length) {
      // clamp right
      return data[data.length - 1].value;
    } else if (price < data[0].strike) {
      // clamp left
      return data[0].value;
    }

    const fraction =
      (price - data[i].strike) / (data[i + 1].strike - data[i].strike);
    return d3.interpolateNumber(data[i].value, data[i + 1].value)(fraction);
  });
}

export function interpolateDataTable(
  data: TableData[],
  prices: number[],
  dte: number,
): number[] {
  const r0 = Math.floor(dte);
  const r1 = r0 + 1;

  if (data.length === 0) {
    return prices.map(() => 0);
  } else if (r0 < 0) {
    const vol0 = data[0];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  } else if (r1 >= data.length) {
    const vol0 = data[data.length - 1];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  }

  const vol0 = data[r0];
  const vol1 = data[r1];

  const row0: TableDataRow[] = Object.keys(vol0.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol0.strikePrices[Number(strike)],
  }));

  const row1: TableDataRow[] = Object.keys(vol1.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol1.strikePrices[Number(strike)],
  }));

  const w1 = dte - r0;
  const w0 = 1 - w1;

  const vol0Interpolated = interpolateDataRow(row0, prices);
  const vol1Interpolated = interpolateDataRow(row1, prices);

  const result: number[] = vol0Interpolated.map(
    (v, i) => v * w0 + vol1Interpolated[i] * w1,
  );

  return result;
}

/**
 * Interpolates volatility data for a specific expiration date across multiple price points.
 *
 * This function performs temporal interpolation of implied volatility data by finding the closest
 * volatility surface data points to the target expiration and interpolating between them.
 * It's essential for options pricing calculations where we need volatility estimates for
 * prices that may not have exact matches in our volatility data for a given expiration date.
 *
 * @param data - Array of volatility surface data, each containing strike prices and their
 *               corresponding implied volatilities for different time-to-expiration values
 * @param prices - Array of underlying asset prices for which we want volatility interpolation
 * @param expiration - Target expiration timestamp (in milliseconds) for which we need volatilities
 *
 * @returns Array of interpolated volatility values corresponding to each price in the prices array.
 *          Returns array of zeros if no data is available or expiration is not found.
 *
 * @example
 * // Get volatilities for prices [100, 105, 110] at a specific expiration
 * const vols = getInterpolatedVolatility(volData, [100, 105, 110], 1640995200000);
 *
 * Algorithm:
 * 1. Finds the exact match for the expiration in the volatility data
 * 2. If exact match found, uses that data point and the next one for interpolation
 * 3. If no exact match, falls back to boundary conditions (first or last available data)
 * 4. Performs temporal interpolation between two volatility surfaces
 * 5. For each surface, performs strike-price interpolation using interpolateDataRow
 * 6. Combines results using weighted average based on time distance
 */
export function getInterpolatedVolatility(
  data: TableData[],
  prices: number[],
  expiration: number,
  useSmoothing: boolean,
): number[] {
  const dte = convertToDaysTillExpiryIntraday(dayjs().valueOf(), expiration);

  const r0 = data.findIndex((d) => d.tte === expiration);
  const r1 = r0 + 1;

  if (data.length === 0 || r0 === -1) {
    return prices.map(() => 0);
  } else if (r0 < 0) {
    const vol0 = data[0];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  } else if (r1 >= data.length) {
    const vol0 = data[data.length - 1];
    const row: TableDataRow[] = Object.keys(vol0.strikePrices).map(
      (strike) => ({
        strike: Number(strike),
        value: vol0.strikePrices[Number(strike)],
      }),
    );
    return interpolateDataRow(row, prices);
  }

  const vol0 = data[r0];
  const vol1 = data[r1];

  const dte0 = convertToDaysTillExpiryIntraday(dayjs().valueOf(), vol0.tte);

  const row0: TableDataRow[] = Object.keys(vol0.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol0.strikePrices[Number(strike)],
  }));

  const row1: TableDataRow[] = Object.keys(vol1.strikePrices).map((strike) => ({
    strike: Number(strike),
    value: vol1.strikePrices[Number(strike)],
  }));

  const w1 = dte - dte0;
  const w0 = 1 - w1;

  const vol0Interpolated = interpolateDataRow(row0, prices);
  const vol1Interpolated = interpolateDataRow(row1, prices);

  const result: number[] = vol0Interpolated.map(
    (v, i) => v * w0 + vol1Interpolated[i] * w1,
  );

  if (useSmoothing) {
    return applyMovingAverage(result);
  }

  return result;
}

export function generatePrices(
  minValue: number,
  maxValue: number,
  numSteps: number = 1_000,
): number[] {
  // Calculate minimum required steps to keep stepSize >= 0.01
  const minRequiredSteps = Math.ceil((maxValue - minValue) / 0.01);
  const adjustedNumSteps = Math.min(numSteps, minRequiredSteps);

  const stepSize = (maxValue - minValue) / (adjustedNumSteps - 1);
  return Array.from(
    { length: adjustedNumSteps },
    (_, i) => minValue + i * stepSize,
  );
}

export function isCall(type: OptionLegType): boolean {
  return [OptionLegType.SHORT_CALL, OptionLegType.LONG_CALL].includes(type);
}

export function isLong(type: OptionLegType): boolean {
  return [OptionLegType.LONG_PUT, OptionLegType.LONG_CALL].includes(type);
}

/**
 * The numeric payload of a `GreeksResult`, minus the WASM object's lifetime
 * members (`free`, `Symbol.dispose`) so plain objects can be returned too.
 */
export type GreeksValues = Pick<
  GreeksResult,
  'price' | 'delta' | 'gamma' | 'vega' | 'theta' | 'rho'
>;

export function calculateOptionPriceAndGreeks(
  currentStockPrice: number,
  strikePrice: number,
  tteYears: number,
  modelParameters: ModelParameters,
  statisticalVolatility: number,
  isCall: boolean,
  exerciseStyle: ExerciseStyleName = 'american',
): GreeksValues {
  const volatility = modelParameters.volatility ?? statisticalVolatility;
  const riskFreeRate = modelParameters.getRiskFreeRate(tteYears);

  // At expiration the option is worth intrinsic value. black76 divides by
  // sigma*sqrt(t), so t == 0 returns NaN when the price equals the strike.
  if (tteYears <= 0) {
    const intrinsic = isCall
      ? Math.max(currentStockPrice - strikePrice, 0)
      : Math.max(strikePrice - currentStockPrice, 0);
    return {
      price: intrinsic,
      delta: intrinsic > 0 ? (isCall ? 1 : -1) : 0,
      gamma: 0,
      vega: 0,
      theta: 0,
      rho: 0,
    };
  }

  return black76(
    currentStockPrice,
    strikePrice,
    tteYears,
    riskFreeRate,
    volatility,
    isCall,
    exerciseStyle,
  );
}

// Engine theta is per year. Within a day of expiry, use minus the remaining time value (dxFeed's convention).
export const getThetaPerDay = (
  greeks: GreeksValues,
  spot: number,
  strike: number,
  isCall: boolean,
  daysToExpiry: number,
): number => {
  if (daysToExpiry <= 0) {
    return 0;
  }
  if (daysToExpiry <= 1) {
    const intrinsic = Math.max(isCall ? spot - strike : strike - spot, 0);
    return -Math.max(greeks.price - intrinsic, 0);
  }
  return greeks.theta / CALENDAR_DAYS_IN_YEAR;
};

export function calculateOptionLegProfit(
  { legType, strikePrice, shares, premium, daysToExpiration }: OptionLeg,
  currentStockPrice: number,
  modelParameters: ModelParameters,
  statisticalVolatility: number,
): number {
  const riskEngineConstants = getRiskEngineConstants();
  const tteYears = daysToExpiration / riskEngineConstants.ADJUSTED_DAYS_IN_YEAR;

  const price = calculateOptionPriceAndGreeks(
    currentStockPrice,
    strikePrice,
    tteYears,
    modelParameters,
    statisticalVolatility,
    isCall(legType),
  ).price;

  const profit = isLong(legType)
    ? price * shares - premium
    : premium - price * shares;

  return profit;
}

export function getShareProfit(
  share: PositionShares,
  currentStockPrice: number,
): number {
  const currentValue = share.quantity * currentStockPrice;
  const costBasis = share.quantity * share.price_paid;
  return currentValue - costBasis;
}

export function getSharesProfit(
  shares: PositionShares[],
  currentStockPrice: number,
): number {
  return shares.reduce((acc, share) => {
    return acc + getShareProfit(share, currentStockPrice);
  }, 0);
}

// Same units as option delta (an ATM call is ~0.50), so 100 shares = 1.00.
export function getSharesDelta(shares: PositionShares[]): number {
  return shares.reduce(
    (acc, share) => acc + share.quantity / CONTRACT_MULTIPLIER,
    0,
  );
}

function getBounds(
  timeframes: TimeframeBoundsPoint[],
  daysToExpiry: number,
): number[] {
  if (!timeframes || timeframes.length === 0) {
    return [0, 0];
  }

  if (daysToExpiry <= timeframes[0].daysToExpiry) {
    return timeframes[0]['bounds-timeframe'];
  }

  for (let i = 0; i < timeframes.length - 1; i++) {
    if (
      daysToExpiry >= timeframes[i].daysToExpiry &&
      daysToExpiry <= timeframes[i + 1].daysToExpiry
    ) {
      const w =
        (daysToExpiry - timeframes[i].daysToExpiry) /
        (timeframes[i + 1].daysToExpiry - timeframes[i].daysToExpiry);

      const b0 =
        timeframes[i]['bounds-timeframe'][0] +
        (timeframes[i + 1]['bounds-timeframe'][0] -
          timeframes[i]['bounds-timeframe'][0]) *
          w;
      const b1 =
        timeframes[i]['bounds-timeframe'][1] +
        (timeframes[i + 1]['bounds-timeframe'][1] -
          timeframes[i]['bounds-timeframe'][1]) *
          w;

      return [Math.min(b0, b1), Math.max(b0, b1)];
    }
  }

  return timeframes[timeframes.length - 1]['bounds-timeframe'];
}

export function calculateProfitLoss(
  optionLegs: OptionLeg[],
  volDataCall: TableData[],
  volDataPut: TableData[],
  modelParameters: ModelParameters,
  priceDomain: number[] | null,
  timeframes: TimeframeBoundsPoint[] | null,
): ProfitLossWithDomain {
  if (optionLegs.length === 0) {
    return {
      profitLossData: [],
      domain: [0, 0],
    };
  }

  const maxStrikePrice = Math.max(
    ...optionLegs.map((option) => option.strikePrice),
  );

  const prices = priceDomain
    ? generatePrices(priceDomain[0], priceDomain[1])
    : generatePrices(0, 2 * maxStrikePrice);

  const priceVolCalls = optionLegs.map((optionLeg) =>
    interpolateDataTable(volDataCall, prices, optionLeg.daysToExpiration),
  );

  const priceVolPuts = optionLegs.map((optionLeg) =>
    interpolateDataTable(volDataPut, prices, optionLeg.daysToExpiration),
  );

  const maxDaysToExpiration = Math.max(
    ...optionLegs.map((optionLeg) => optionLeg.daysToExpiration),
  );
  const ivBounds = timeframes
    ? getBounds(timeframes, maxDaysToExpiration)
    : null;

  const allProfitLossData: ProfitLossData[] = prices.map((price, index) => {
    let profit: number = 0.0;
    optionLegs.forEach((optionLeg, optionIndex) => {
      profit += calculateOptionLegProfit(
        optionLeg,
        price,
        modelParameters,
        (isCall(optionLeg.legType)
          ? priceVolCalls[optionIndex]
          : priceVolPuts[optionIndex])[index],
      );
    });

    const profitBounds = [0.0, 0.0];
    if (ivBounds) {
      optionLegs.forEach((optionLeg, optionIndex) => {
        profitBounds[0] += calculateOptionLegProfit(
          optionLeg,
          price,
          { ...modelParameters, volatility: ivBounds[0] },
          (isCall(optionLeg.legType)
            ? priceVolCalls[optionIndex]
            : priceVolPuts[optionIndex])[index],
        );
        profitBounds[1] += calculateOptionLegProfit(
          optionLeg,
          price,
          { ...modelParameters, volatility: ivBounds[1] },
          (isCall(optionLeg.legType)
            ? priceVolCalls[optionIndex]
            : priceVolPuts[optionIndex])[index],
        );
      });
    } else {
      profitBounds[0] = profit;
      profitBounds[1] = profit;
    }

    return {
      price,
      profit,
      volatility:
        modelParameters.volatility ??
        (priceVolCalls.length > 0 ? priceVolCalls[0][index] : 0),
      profitBounds,
    };
  });

  // Domain is defined as the region where the PnL line is profit->loss or loss->profit.
  const getProfitDomain = (profitLossData: ProfitLossData[]): number[] => {
    let minDomain: number | null = null;
    let maxDomain: number | null = null;

    for (let i = 0; i < profitLossData.length - 1; i++) {
      if (
        (profitLossData[i].profit >= 0 && profitLossData[i + 1].profit < 0) ||
        (profitLossData[i].profit < 0 && profitLossData[i + 1].profit >= 0)
      ) {
        if (minDomain === null) {
          minDomain = profitLossData[i].price;
        } else {
          maxDomain = profitLossData[i + 1].price;
        }
      }
    }

    if (minDomain === null || maxDomain === null) {
      return [
        0,
        profitLossData.length > 0
          ? profitLossData[profitLossData.length - 1].price
          : 0,
      ];
    }

    let domainWindow = maxDomain - minDomain;
    domainWindow = Math.max(1, domainWindow * 0.15); // MINIMUM_CHART_X_DOMAIN placeholder
    minDomain -= domainWindow;
    maxDomain += domainWindow;

    minDomain = Math.floor(minDomain);
    maxDomain = Math.ceil(maxDomain);

    if (minDomain < 0) {
      minDomain = 0;
    }

    return [minDomain, maxDomain];
  };

  const [minPriceDomain, maxPriceDomain] = getProfitDomain(allProfitLossData);
  const chartProfitLossData = allProfitLossData.filter(
    (data) => data.price >= minPriceDomain && data.price <= maxPriceDomain,
  );

  return {
    profitLossData: chartProfitLossData,
    domain: [minPriceDomain, maxPriceDomain],
  };
}

/**
 * Calculate option intrinsic value at expiration
 */
const getOptionIntrinsicValue = (
  strike: number,
  underlyingPrice: number,
  optionType: OptionType,
): number => {
  return Math.max(
    0,
    (underlyingPrice - strike) * (optionType === OptionType.CALL ? 1 : -1),
  );
};

/**
 * Calculate profit/loss for a single option leg at expiration
 */
const getOptionLegExpirationPnL = (
  leg: PositionLeg,
  underlyingPrice: number,
): number => {
  const intrinsicValue = getOptionIntrinsicValue(
    leg.strike,
    underlyingPrice,
    leg.option_type,
  );
  // For long positions (quantity > 0): profit = (intrinsic value - premium paid) * quantity * multiplier
  // For short positions (quantity < 0): profit = (premium received - intrinsic value) * |quantity| * multiplier
  if (leg.quantity > 0) {
    // Long position
    return (
      (intrinsicValue - leg.price_paid) * leg.quantity * CONTRACT_MULTIPLIER
    );
  }

  // Short position
  return (
    (leg.price_paid - intrinsicValue) *
    Math.abs(leg.quantity) *
    CONTRACT_MULTIPLIER
  );
};

/**
 * Analyze position structure to determine unlimited profit/loss potential
 */
const analyzePositionLimits = (
  optionLegs: PositionLeg[],
  shares: PositionShares[],
): {
  hasUnlimitedUpside: boolean;
  hasUnlimitedDownside: boolean;
  netPutPosition: number;
} => {
  // Calculate net positions
  const netSharePosition = shares.reduce(
    (sum, share) => sum + share.quantity,
    0,
  );

  let netCallPosition = 0;
  let netPutPosition = 0;
  for (const leg of optionLegs) {
    if (leg.option_type === OptionType.CALL) {
      netCallPosition += leg.quantity;
    } else {
      netPutPosition += leg.quantity;
    }
  }

  // Above every strike, calls and shares offset (a covered call is capped).
  const upsideSharesEquivalent =
    netCallPosition * CONTRACT_MULTIPLIER + netSharePosition;
  const hasUnlimitedUpside = upsideSharesEquivalent > 0;
  const hasUnlimitedDownside = upsideSharesEquivalent < 0;

  return { hasUnlimitedUpside, hasUnlimitedDownside, netPutPosition };
};

/**
 * Find all breakeven prices where profit crosses zero
 */
const findBreakevenPrices = (profitLossData: ProfitLossData[]): number[] => {
  const breakEvenPrices: number[] = [];

  for (let i = 0; i < profitLossData.length - 1; i++) {
    const current = profitLossData[i];
    const next = profitLossData[i + 1];

    // Check if profit crosses zero between these points
    if (current.profit < 0 != next.profit < 0) {
      // Linear interpolation for more precise breakeven
      if (Math.abs(current.profit - next.profit) > 1e-10) {
        const ratio =
          Math.abs(current.profit) / Math.abs(current.profit - next.profit);
        const breakEven = current.price + ratio * (next.price - current.price);
        breakEvenPrices.push(breakEven);
      } else {
        // If profits are essentially equal, use midpoint
        breakEvenPrices.push(d3.mean([current.price, next.price])!);
      }
    }
  }

  // Remove duplicates and sort
  return [...new Set(breakEvenPrices)].sort((a, b) => a - b);
};

/**
 * Calculate maximum profit considering position limits
 */
const calculateMaxProfit = (
  profitLossData: ProfitLossData[],
  optionLegs: PositionLeg[],
  shares: PositionShares[],
  domain: number[],
): number => {
  // For limited profit potential, find the maximum from calculated data
  let maxProfit = -Infinity;

  for (const data of profitLossData) {
    maxProfit = Math.max(maxProfit, data.profit);
  }

  // Also check at extreme prices beyond our domain for edge cases
  const extremeHighPrice = domain[1] * 2;
  const extremeLowPrice = Math.max(0, domain[0] * 0.1);

  const highPriceProfit =
    optionLegs.reduce(
      (sum, leg) => sum + getOptionLegExpirationPnL(leg, extremeHighPrice),
      0,
    ) + getSharesProfit(shares, extremeHighPrice);

  const lowPriceProfit =
    optionLegs.reduce(
      (sum, leg) => sum + getOptionLegExpirationPnL(leg, extremeLowPrice),
      0,
    ) + getSharesProfit(shares, extremeLowPrice);

  // Long puts and short shares make the most if the stock goes to $0.
  const zeroPriceProfitDollars =
    optionLegs.reduce(
      (sum, leg) => sum + getOptionLegExpirationPnL(leg, 0),
      0,
    ) + getSharesProfit(shares, 0);

  maxProfit = Math.max(
    maxProfit,
    highPriceProfit,
    lowPriceProfit,
    zeroPriceProfitDollars,
  );

  return maxProfit;
};

/**
 * Calculate maximum loss considering position limits
 */
const calculateMaxLoss = (
  profitLossData: ProfitLossData[],
  optionLegs: PositionLeg[],
  shares: PositionShares[],
  domain: number[],
): number => {
  // For limited loss potential, find the minimum from calculated data
  let maxLoss = Infinity;

  for (const data of profitLossData) {
    maxLoss = Math.min(maxLoss, data.profit);
  }

  // Also check at extreme prices beyond our domain
  const extremeHighPrice = domain[1] * 2;
  const extremeLowPrice = Math.max(0, domain[0] * 0.1);

  const highPriceProfit =
    optionLegs.reduce(
      (sum, leg) => sum + getOptionLegExpirationPnL(leg, extremeHighPrice),
      0,
    ) + getSharesProfit(shares, extremeHighPrice);

  const lowPriceProfit =
    optionLegs.reduce(
      (sum, leg) => sum + getOptionLegExpirationPnL(leg, extremeLowPrice),
      0,
    ) + getSharesProfit(shares, extremeLowPrice);

  maxLoss = Math.min(maxLoss, highPriceProfit, lowPriceProfit);

  // Long shares and short puts lose the most if the stock goes to $0.
  const lossAtZero =
    optionLegs.reduce(
      (sum, leg) => sum + getOptionLegExpirationPnL(leg, 0),
      0,
    ) + getSharesProfit(shares, 0);
  maxLoss = Math.min(maxLoss, lossAtZero);

  // Ensure max loss is never positive (losses should be negative or zero)
  return Math.min(maxLoss, 0);
};

/**
 * PnL metrics calculation
 */
const calculatePnlMetrics = (
  allProfitLossData: ProfitLossData[],
  optionLegs: PositionLeg[],
  shares: PositionShares[],
  domain: number[],
) => {
  // Analyze position structure
  const { hasUnlimitedUpside, hasUnlimitedDownside, netPutPosition } =
    analyzePositionLimits(optionLegs, shares);

  // Calculate max profit and max loss
  const maxProfit = hasUnlimitedUpside
    ? Infinity
    : calculateMaxProfit(allProfitLossData, optionLegs, shares, domain);

  const maxLoss = hasUnlimitedDownside
    ? -Infinity
    : calculateMaxLoss(allProfitLossData, optionLegs, shares, domain);

  // Find all breakeven prices
  const breakEvenPrices = findBreakevenPrices(allProfitLossData);

  return {
    maxProfit,
    maxLoss,
    breakEvenPrices,
    netPutPosition,
  };
};

export function getProfitLoss(
  optionLegs: PositionLeg[],
  shares: PositionShares[],
  volDataCall: TableData[],
  volDataPut: TableData[],
  allStrikes: number[],
  modelParameters: ModelParameters,
): ProfitLossWithDomain {
  if (optionLegs.length === 0 && shares.length === 0) {
    return {
      profitLossData: [],
      domain: [0, 0],
      maxProfit: 0,
      maxLoss: 0,
      breakEvenPrices: [],
      netPutPosition: 0,
    };
  }

  let domain: [number, number];
  const now = dayjs().tz(ET).valueOf();

  if (optionLegs.length > 0) {
    // Use option strikes to determine domain
    const minStrikePrice = Math.min(
      modelParameters.currentPrice ?? Infinity,
      ...optionLegs.map((option) => option.strike),
    );
    const maxStrikePrice = Math.max(
      modelParameters.currentPrice ?? -Infinity,
      ...optionLegs.map((option) => option.strike),
    );
    // default to 50% of the range on each side
    domain = [minStrikePrice * 0.5, maxStrikePrice * 1.5];
  } else {
    // Only shares - use current price from model parameters as reference
    const currentPrice = modelParameters.currentPrice || 100;
    // Create a reasonable range around current price for shares-only positions
    domain = [0, currentPrice * 2.0];
  }

  const requestedSamplePrices = modelParameters.samplePrices;
  let prices: number[];
  if (requestedSamplePrices != null) {
    prices = [...new Set(requestedSamplePrices)].sort((a, b) => a - b);
    // The returned domain must describe every explicitly requested sample.
    domain = [
      Math.min(domain[0], prices[0]),
      Math.max(domain[1], prices[prices.length - 1]),
    ];
  } else {
    // Merge optionLeg strikes into prices to ensure "clean edges" in 0DTE 'simple" PnL
    // const prices = generatePrices(domain[0], domain[1]);
    const strikes = [
      ...new Set([...allStrikes, ...optionLegs.map((l) => l.strike)]),
    ];
    prices = [...generatePrices(domain[0], domain[1]), ...strikes].sort(
      (a, b) => a - b,
    );
  }

  const optionVariables = optionLegs.map((optionLeg) => {
    const baseDate = modelParameters.baseDate ?? now;
    const tteYears = getTteYearsFromBaseDate(baseDate, optionLeg.expiry);
    return {
      volatility: getVolFromTableData(
        optionLeg.option_type === OptionType.CALL ? volDataCall : volDataPut,
        optionLeg.strike,
        optionLeg.expiry,
      ),
      tteYears,
      originalPremium: optionLeg.price_paid,
      quantity: optionLeg.quantity,
      isCall: optionLeg.option_type === OptionType.CALL,
      strike: optionLeg.strike,
    };
  });

  const bsmResults: GreeksValues[][] = optionLegs.map(
    (optionLeg, optionIndex) => {
      const { tteYears, volatility } = optionVariables[optionIndex];

      return prices.map((price) =>
        calculateOptionPriceAndGreeks(
          price,
          optionLeg.strike,
          tteYears,
          modelParameters,
          volatility,
          optionLeg.option_type === OptionType.CALL,
        ),
      );
    },
  );

  const allProfitLossData: ProfitLossData[] = prices.map((price, index) => {
    let profit: number = 0.0;
    let delta: number = 0.0;
    let gamma: number = 0.0;
    let vega: number = 0.0;
    let theta: number = 0.0;
    optionLegs.forEach((optionLeg, optionIndex) => {
      const originalPremium = optionLeg.price_paid;
      const bsmResult: GreeksValues = bsmResults[optionIndex][index];
      const currentPrice = bsmResult.price;
      delta += bsmResult.delta * optionLeg.quantity;
      gamma += bsmResult.gamma * optionLeg.quantity;
      vega += (bsmResult.vega / 100) * optionLeg.quantity;
      theta +=
        getThetaPerDay(
          bsmResult,
          price,
          optionLeg.strike,
          optionLeg.option_type === OptionType.CALL,
          (optionLeg.expiry - (modelParameters.baseDate ?? now)) /
            MILLISECONDS_PER_DAY,
        ) * optionLeg.quantity;

      // For long positions (quantity > 0): profit = (current_price - original_price) * quantity
      // For short positions (quantity < 0): profit = (original_price - current_price) * |quantity|
      const localProfit =
        optionLeg.quantity > 0
          ? (currentPrice - originalPremium) * optionLeg.quantity
          : (originalPremium - currentPrice) * Math.abs(optionLeg.quantity);

      profit += localProfit * 100; // convert to dollars since option prices are multiples of 100
    });

    profit += getSharesProfit(shares, price);
    delta += getSharesDelta(shares);

    const profitBounds = [profit, profit];

    return {
      price,
      profit,
      profitBounds,
      delta,
      gamma,
      vega,
      theta,
    };
  });

  const chartProfitLossData =
    requestedSamplePrices == null
      ? allProfitLossData.filter(
          (data) => data.price >= domain[0] && data.price <= domain[1],
        )
      : allProfitLossData;

  const { maxProfit, maxLoss, breakEvenPrices, netPutPosition } =
    calculatePnlMetrics(allProfitLossData, optionLegs, shares, domain);

  return {
    profitLossData: chartProfitLossData,
    domain,
    maxProfit,
    maxLoss,
    breakEvenPrices,
    netPutPosition,
  };
}

export const getVolFromTableData = (
  tableDatas: TableData[],
  strike: number,
  tte: number,
) => {
  const tableData = tableDatas.find((data) => data.tte === tte);
  if (tableData == null) {
    return 0;
  }
  return tableData.strikePrices[strike];
};

export const getTteYearsFromBaseDate = (
  baseDate: number,
  expiry: number,
): number => {
  const { SECONDS_PER_DAY } = getRiskEngineConstants();
  const timeFrameStart = dayjs(baseDate);
  const daysTillExpiration = (expiry - baseDate) / MILLISECONDS_PER_DAY;

  return calculateVarianceTime(
    timeFrameStart.unix(),
    Array.from(CLOSURES),
    daysTillExpiration * SECONDS_PER_DAY,
    VarianceTimeCalendarModel.BusinessDayModel,
  );
};

export const getSgGreeksFromDxFeedGreeks = (
  greeksData: RawGreeksDataMap,
  currentPrice: number,
  getRiskFreeRate: (tte: number) => number,
  callVolatilities: TableData[],
  putVolatilities: TableData[],
  exerciseStyles: ExerciseStylesRecord | null = null,
): RawGreeksDataMap => {
  const now = dayjs().tz(ET).valueOf();
  const sgGreeksData: RawGreeksDataMap = {};
  for (const tte in greeksData) {
    const exerciseStyle = resolveStyleForExpiry(exerciseStyles, Number(tte));
    const daysToExpiry = (Number(tte) - now) / MILLISECONDS_PER_DAY;
    const strikeToGreeks: RawGreeksObject = greeksData[tte];
    const sgPriceLevel: RawGreeksObject = {};
    for (const strike in strikeToGreeks) {
      const yte = getTteYearsFromBaseDate(now, Number(tte));
      const callVolatility =
        callVolatilities.find((data) => data.tte === Number(tte))?.strikePrices[
          Number(strike)
        ] ?? 0;
      const putVolatility =
        putVolatilities.find((data) => data.tte === Number(tte))?.strikePrices[
          Number(strike)
        ] ?? 0;
      const call = strikeToGreeks[Number(strike)][CALL];
      const put = strikeToGreeks[Number(strike)][PUT];
      const sgCallGreeks: GreeksValues = calculateOptionPriceAndGreeks(
        currentPrice,
        Number(strike),
        yte,
        { getRiskFreeRate } as ModelParameters,
        callVolatility,
        true,
        exerciseStyle,
      );
      const sgPutGreeks: GreeksValues = calculateOptionPriceAndGreeks(
        currentPrice,
        Number(strike),
        yte,
        { getRiskFreeRate } as ModelParameters,
        putVolatility,
        false,
        exerciseStyle,
      );
      sgPriceLevel[strike] = [
        [
          call[GREEK_IDX.BID],
          call[GREEK_IDX.ASK],
          sgCallGreeks.delta,
          sgCallGreeks.gamma,
          getThetaPerDay(
            sgCallGreeks,
            currentPrice,
            Number(strike),
            true,
            daysToExpiry,
          ),
          sgCallGreeks.vega / 100,
          callVolatility,
          call[GREEK_IDX.TIMESTAMP],
        ], // CALL
        [
          put[GREEK_IDX.BID],
          put[GREEK_IDX.ASK],
          sgPutGreeks.delta,
          sgPutGreeks.gamma,
          getThetaPerDay(
            sgPutGreeks,
            currentPrice,
            Number(strike),
            false,
            daysToExpiry,
          ),
          sgPutGreeks.vega / 100,
          putVolatility,
          put[GREEK_IDX.TIMESTAMP],
        ], // PUT
      ];
    }
    sgGreeksData[Number(tte)] = sgPriceLevel;
  }
  return sgGreeksData;
};
